import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Divider,
  alpha,
  useTheme,
  Button,
  Paper,
  Link
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  DataUsage as DataUsageIcon,
  Timeline as TimelineIcon,
  Lightbulb as LightbulbIcon,
  Person as PersonIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const AboutUsPage = () => {
  const theme = useTheme();
  const teamMembers = [
    {
      name: 'Mathew',
      role: 'CEO & Chief Architect',
      bio: "Mathew brings his expertise in machine learning and financial markets to lead our technical vision. His passion for AI-driven solutions guides our innovative approach to investment technology.",
      icon: <PsychologyIcon />
    },
    {
      name: 'Stevin',
      role: 'COO',
      bio: "Stevin oversees operations and compliance, ensuring our platform meets the highest standards. His attention to detail and focus on data quality are essential to our success.",
      icon: <GroupsIcon />
    },
    {
      name: 'Leslie',
      role: 'Strategic Advisor',
      bio: "Leslie crafts our brand messaging and builds strategic partnerships. With his background in investment strategy and marketing, he helps make our complex technology accessible to everyone.",
      icon: <TimelineIcon />
    },
    {
      name: 'Arya',
      role: 'Lead AI Designer & Developer',
      bio: "Arya specializes in creating intuitive user experiences that make our advanced technology approachable. She bridges the gap between complex AI systems and user-friendly design leveraging the latest AI tools for design & implementation.",
      icon: <LightbulbIcon />
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Simple Header */}
      <Box 
        sx={{ 
          bgcolor: theme.palette.primary.main, 
          color: 'white',
          py: 6
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            About Us
          </Typography>
          <Typography 
            variant="h5"
            sx={{ 
              fontWeight: 400,
              maxWidth: 800,
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            Reimagine investing with AI insights.
          </Typography>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
            Level the playing field with AI insights
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', textAlign: 'center' }}>
            The financial landscape is constantly evolving, with rapid changes that can seem daunting to keep up with. Whether you're managing vast client portfolios or investing your own portfolio, the need for robust, real-time data and predictive analytics is universal. Our AI insights engine is designed to predict stock market trends and accelerate equity research, giving everyone from individual investors to professional advisors the tools they need to stay ahead.
          </Typography>
          <Divider sx={{ my: 2 }} />
        </Box>
      </Container>

      {/* Our Vision & Mission Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), py: 5 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
              Our Vision & Mission
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', mb: 4 }}>
              We envision a world where advanced AI technology makes investing more accessible, transparent, and effective for everyone. Our mission is to empower investors by providing sophisticated tools that simplify complex decisions and offer insights previously available only to large institutions.
            </Typography>
            <Divider sx={{ maxWidth: 100, mx: 'auto', my: 4, borderColor: theme.palette.secondary.main, borderWidth: 2 }} />
            <Typography variant="body1" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>
              "We're building technology that gives people more confidence in their financial decisions while freeing up time to focus on what truly matters in their lives."
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              {
                title: "Data-Driven Insights",
                description: "We combine vast quantities of market data with sophisticated algorithms to identify patterns and opportunities.",
                icon: <DataUsageIcon fontSize="large" />,
                color: theme.palette.primary.main
              },
              {
                title: "Transparency First",
                description: "We clearly explain how our technology works and the reasoning behind every insight we provide.",
                icon: <LightbulbIcon fontSize="large" />,
                color: theme.palette.secondary.main
              },
              {
                title: "Advanced Predictions",
                description: "Our forecasting models analyze market trends across multiple time horizons with remarkable accuracy.",
                icon: <TimelineIcon fontSize="large" />,
                color: theme.palette.success.main
              },
              {
                title: "Human-Centered Design",
                description: "We create intuitive interfaces that make complex financial data understandable and actionable.",
                icon: <PsychologyIcon fontSize="large" />,
                color: theme.palette.info.main
              }
            ].map((value, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      color: value.color
                    }}
                  >
                    {value.icon}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                      {value.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Approach Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            Our Approach
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
            We believe that technology should enhance human decision-making, not replace it. Our approach combines cutting-edge machine learning with intuitive design to create tools that provide clarity and confidence.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
            Every insight comes with clear explanations of the underlying analysis. We're committed to transparency—helping you understand not just what our technology recommends, but why.
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            Our development process involves continuous collaboration between our financial experts, data scientists, and most importantly, feedback from our community of users. This ensures that our platform evolves to meet real investor needs.
          </Typography>
        </Box>
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            bgcolor: alpha(theme.palette.secondary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
            mb: 6
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
            Our Platform Features:
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {[
              "Advanced AI-driven stock analysis",
              "Clear, actionable insights",
              "Customizable portfolio tracking",
              "Intuitive research tools",
              "Real-time market data",
              "Transparent methodology"
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    mr: 2, 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: theme.palette.secondary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    {index + 1}
                  </Box>
                  <Typography variant="body1">{feature}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Meet the Team Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
              Founding Team
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', maxWidth: 800, mx: 'auto' }}>
              Our co-founders bring 30+ years of collective experience in investment strategy, product design, data science, and AI research. Since 2018, we've been meticulously crafting SMA Insights with one goal in mind: to give financial advisors and active investors a powerful edge. What sets us apart is our relentless commitment to AI innovation, creating an intuitive platform, strategic stock selection, and transparency in performance.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {teamMembers.map((member) => (
              <Grid item xs={12} sm={6} key={member.name}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      mb: 0
                    }}>
                      {member.icon || <PersonIcon fontSize="large" />}
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 3, flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold">{member.name}</Typography>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {member.role}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      {member.bio}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Values Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              Our Values
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', textAlign: 'center', mb: 5 }}>
              At Seattle Market Analytics, our core values are designed to serve one purpose: empowering everyday investors with confidence, clarity, and intelligent tools.
            </Typography>
            
            <Grid container spacing={3}>
              {[
                {
                  title: "Clarity Over Complexity",
                  description: "We believe investing tools should explain, not confuse. Our platform is built to deliver insights in plain language, with full transparency on how our AI works—no black boxes, no guesswork."
                },
                {
                  title: "Trust Through Transparency",
                  description: "We're upfront about what our technology can and cannot do. We show our data sources, methodologies, and performance—because trust is earned through openness."
                },
                {
                  title: "Innovation That Works for You",
                  description: "AI should simplify decisions, not overcomplicate them. We use cutting-edge tools to surface insights that actually matter—no hype, just real utility for your financial goals."
                },
                {
                  title: "Access Without Barriers",
                  description: "You shouldn't need a finance degree to make smart decisions. Our platform is built for anyone—whether you're managing a portfolio or just getting started."
                },
                {
                  title: "Built with You, Not Just for You",
                  description: "Your feedback shapes our roadmap. We listen, learn, and iterate—because the best investing platform is one that evolves with its users."
                }
              ].map((value, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2">
                      {value.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* Commitment Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), py: 6 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Our Commitment
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', fontStyle: 'italic', maxWidth: 800, mx: 'auto' }}>
            "We're committed to building technology that empowers better financial decisions—tools that provide clarity in a complex landscape and make sophisticated investing accessible to all."
          </Typography>
          <Typography variant="body2" color="text.secondary">
            — The Seattle Market Analytics Team
          </Typography>
        </Container>
      </Box>

      {/* Simple Contact Section */}
      <Box sx={{ bgcolor: theme.palette.primary.main, color: 'white', py: 6 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Connect With Us
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Have questions about our team or approach? We'd love to hear from you.
          </Typography>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large"
            component={RouterLink}
            to="/contact"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1
            }}
          >
            Contact Us
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUsPage; 